/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
  output,
  TemplateRef,
  viewChild,
  ViewContainerRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  Subscription
} from 'rxjs';

import { UmssAvatarComponent } from '../avatar/avatar.component';
import {
  DecorationType,
  UmssDecorationComponent
} from '../decoration/decoration.component';
import {
  OverlayScrollBehavior,
  OverlayScrollService
} from '../../services/overlay-scroll.service';
import { DropdownPortalService } from '../../services/dropdown-portal.service';
import { UmssIconButtonComponent } from '../icon-button/icon-button.component';
import { UmssIconComponent } from '../icon/icon.component';
import { UmssLabelComponent } from '../label/label.component';
import { UmssMenuGroupLabelComponent } from '../menu-group-label/menu-group-label.component';
import { UmssMenuItemComponent } from '../menu-item/menu-item.component';
import { UmssMenuComponent } from '../menu/menu.component';
import { UmssSearchBarComponent } from '../search/search-bar.component';

export interface SelectLoading {
  selector?: boolean;
  menu?: boolean;
  options?: boolean;
}
export interface SelectOption<T = any> {
  value: T;
  label: string;
  disabled?: boolean;
  iconName?: string;
  description?: string;
  shortcut?: string;
  avatar?: {
    src?: string;
    name?: string;
    initials?: string;
    status?: 'Online' | 'Offline' | 'Away' | 'Busy';
  };
  decoration?: {
    type?: DecorationType;
    text?: string;
    icon?: string;
    color?: string;
    backgroundColor?: string;
  };
  group?: string;
}

@Component({
  selector: 'umss-select',
  standalone: true,
  imports: [
    CommonModule,
    UmssMenuComponent,
    UmssMenuItemComponent,
    UmssMenuGroupLabelComponent,
    UmssAvatarComponent,
    UmssDecorationComponent,
    UmssLabelComponent,
    UmssIconComponent,
    UmssIconButtonComponent,
    UmssSearchBarComponent
  ],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssSelectComponent),
      multi: true
    }
  ]
})
export class UmssSelectComponent<T = unknown>
  implements ControlValueAccessor, OnDestroy
{
  private elementRef = inject(ElementRef);
  private overlayScrollService = inject(OverlayScrollService);
  private viewContainerRef = inject(ViewContainerRef);
  private dropdownPortal = inject(DropdownPortalService);

  @HostBinding('style.display') display = 'contents';

  readonly listboxId = `umss-select-listbox-${Math.random().toString(36).slice(2)}`;

  // Refs
  optionsContainer = viewChild<ElementRef<HTMLElement>>('optionsContainer');
  searchContainer = viewChild<ElementRef<HTMLElement>>('searchContainer');
  private readonly dropdownPortalTpl =
    viewChild.required<TemplateRef<void>>('dropdownPortalTpl');

  // Inputs
  options = input<SelectOption<T>[]>([]);
  placeholder = input('Select an option');
  label = input<string | undefined>(undefined);
  helperText = input<string | undefined>(undefined);
  errorMessage = input<string | undefined>(undefined);
  clearable = input(false);
  searchable = input(false);

  size = input<'Small' | 'Regular' | 'Large'>('Regular');
  disabled = input(false);
  required = input(false);
  error = input(false);
  filterStyle = input<'outlined' | 'elevated'>('outlined');

  loading = input<SelectLoading>({
    selector: false,
    menu: false,
    options: false
  });
  multiple = input(false);
  hideSelectedOptions = input(false);
  isPaginated = input(false);
  scrollBehavior = input<OverlayScrollBehavior>('close');

  // Model
  value = model<T | T[] | null>(null);

  // Outputs
  selectionChange = output<T | T[] | null>();
  searchChange = output<string | null>();
  onFocus = output<void>();
  onBlur = output<void>();
  onScroll = output<number>();

  // Internal State
  _isOpen: boolean = false;
  private _isDisabled = false; // from ControlValueAccessor
  protected lastSearchTerm: string | null = null;
  protected verticalPosition: 'top' | 'bottom' = 'bottom';
  protected horizontalPosition: 'left' | 'right' = 'left';
  protected dropdownStyle: Record<string, string> = {};
  private scrollSub?: Subscription;
  private _overlayScrollId?: symbol;
  private dropdownOutlet?: DomPortalOutlet;
  private dropdownContainer?: HTMLElement;

  // CVA
  private _onChange = (_: T | T[] | null) => {};
  private _onTouched = () => {};

  get isOpen(): boolean {
    return this._isOpen;
  }

  get isComponentDisabled(): boolean {
    return this.disabled() || this._isDisabled;
  }

  selectedOption = computed(() => {
    if (this.multiple()) return null;
    return this.options().find((o) => o.value === this.value()) || null;
  });

  selectedOptions = computed(() => {
    if (!this.multiple()) return [];
    const val = this.value();
    const values = Array.isArray(val) ? val : val ? [val] : [];
    return this.options().filter((o) => values.includes(o.value));
  });

  displayText = computed(() => {
    if (this.multiple()) {
      const selected = this.selectedOptions();
      if (selected.length === 0) return this.placeholder();
      if (selected.length === 1) return selected[0].label;
      return `${selected.length} selected`;
    }
    return this.selectedOption()?.label || this.placeholder();
  });

  filteredOptions = computed(() => {
    if (!this.hideSelectedOptions()) return this.options();

    if (this.multiple()) {
      const val = this.value();
      const selectedValues = Array.isArray(val) ? val : val ? [val] : [];
      return this.options().filter((o) => !selectedValues.includes(o.value));
    } else {
      return this.options().filter((o) => o.value !== this.value());
    }
  });

  groupedOptions = computed(() => {
    const grouped: { [group: string]: SelectOption[] } = { default: [] };
    this.filteredOptions().forEach((o) => {
      const groupName = o.group || 'default';
      if (!grouped[groupName]) grouped[groupName] = [];
      grouped[groupName].push(o);
    });
    return grouped;
  });

  hasGroups = computed(() => this.options().some((o) => o.group));

  groupNames = computed(() =>
    Object.keys(this.groupedOptions()).filter((g) => g !== 'default')
  );

  constructor() {
    effect(() => {
      const container = this.optionsContainer();
      if (container && this.isPaginated() && !this.scrollSub) {
        this.subscribeToScroll(container);
      } else if (!this.isPaginated() && this.scrollSub) {
        this.scrollSub.unsubscribe();
        this.scrollSub = undefined;
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    const clickedInside =
      this.elementRef.nativeElement.contains(target) ||
      (this.dropdownContainer?.contains(target) ?? false);
    if (!clickedInside && this._isOpen) {
      this.onClickOutside();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this._isOpen) {
      this.calculatePosition();
    }
  }

  ngOnDestroy(): void {
    this.scrollSub?.unsubscribe();
    this._unregisterScrollBehavior();
    this.dropdownOutlet?.dispose();
  }

  // CVA
  writeValue(val: T | T[] | null): void {
    if (this.multiple() && val && !Array.isArray(val)) {
      this.value.set([val]);
    } else if (this.multiple() && !val) {
      this.value.set([]);
    } else {
      this.value.set(val);
    }
  }

  registerOnChange(fn: (value: T | T[] | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._isDisabled = isDisabled;
  }

  onToggleDropdown(): void {
    if (this.isComponentDisabled || this.loading().selector) return;

    if (this._isOpen) {
      this._isOpen = false;
      this.dropdownOutlet?.detach();
    } else {
      this._isOpen = true;
      this.onFocus.emit();
      this.calculatePosition();
      this._registerScrollBehavior();
      this._attachDropdownPortal();
      if (this.searchable()) {
        setTimeout(() => {
          const input =
            this.searchContainer()?.nativeElement.querySelector('input');
          input?.focus();
        }, 0);
      }
    }
  }

  onSelectOption(option: SelectOption<T>): void {
    if (option.disabled) return;

    if (this.multiple()) {
      const val = this.value();
      const currentValues = Array.isArray(val) ? [...val] : val ? [val] : [];
      const index = currentValues.indexOf(option.value);

      if (index > -1) {
        currentValues.splice(index, 1);
      } else {
        currentValues.push(option.value);
      }

      this.value.set(currentValues);
      this._onChange(currentValues);
      this.selectionChange.emit(currentValues);
    } else {
      this.value.set(option.value);
      this._onChange(option.value);
      this.selectionChange.emit(option.value);
      this._isOpen = false;
      this.dropdownOutlet?.detach();
      this._onTouched();
      this.onBlur.emit();
      this._unregisterScrollBehavior();
    }
  }

  onClickOutside(): void {
    this._isOpen = false;
    this.dropdownOutlet?.detach();
    this._onTouched();
    this.onBlur.emit();
    this._unregisterScrollBehavior();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.isComponentDisabled || this.loading().selector) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.onToggleDropdown();
        break;
      case 'Escape':
        this._isOpen = false;
        this.dropdownOutlet?.detach();
        this._onTouched();
        this._unregisterScrollBehavior();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this._isOpen) {
          this._isOpen = true;
          this.calculatePosition();
          this._registerScrollBehavior();
          this._attachDropdownPortal();
        } else {
          this.navigateOptions(1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this._isOpen) {
          this.navigateOptions(-1);
        }
        break;
    }
  }

  private navigateOptions(direction: number): void {
    const enabledOptions = this.options().filter((o) => !o.disabled);
    if (enabledOptions.length === 0) return;

    const val = this.value();
    const currentIndex = enabledOptions.findIndex((o) => o.value === val);
    let newIndex = currentIndex + direction;

    if (newIndex < 0) {
      newIndex = enabledOptions.length - 1;
    } else if (newIndex >= enabledOptions.length) {
      newIndex = 0;
    }

    const newVal = enabledOptions[newIndex].value;
    this.value.set(newVal);
    this._onChange(newVal);
  }

  trackByValue(index: number, option: SelectOption<T>): T {
    return option.value;
  }

  isOptionSelected(option: SelectOption<T>): boolean {
    const val = this.value();
    if (this.multiple()) {
      const values = Array.isArray(val) ? val : [];
      return values.includes(option.value);
    }
    return val === option.value;
  }

  onClear(event: MouseEvent): void {
    if (!this.clearable()) return;
    event.stopPropagation();

    this.value.set(null);
    this._onChange(null);
    this.selectionChange.emit(null);
  }

  onSearch(term: string | null): void {
    if (!this.searchable()) return;
    this.searchChange.emit(term);

    const container = this.optionsContainer();
    if (container) {
      container.nativeElement.scrollTop = 0;
    }
    this.lastSearchTerm = term;
  }

  private onScrollEvent(container: HTMLElement): number {
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight - container.clientHeight;
    return Math.round((scrollTop / scrollHeight) * 100);
  }

  private subscribeToScroll(ref: ElementRef<HTMLElement>): void {
    const container = ref.nativeElement;
    this.scrollSub = fromEvent(container, 'scroll')
      .pipe(
        filter(() => !this.loading().options),
        debounceTime(100),
        distinctUntilChanged(),
        map(() => this.onScrollEvent(container))
      )
      .subscribe((percent) => {
        this.onScroll.emit(percent);
      });
  }

  private _registerScrollBehavior(): void {
    this._unregisterScrollBehavior();
    this._overlayScrollId = this.overlayScrollService.register({
      behavior: this.scrollBehavior(),
      onFollow: () => this.calculatePosition(),
      onClose: () => {
        this._isOpen = false;
        this.dropdownOutlet?.detach();
        this._onTouched();
        this.onBlur.emit();
        this._unregisterScrollBehavior();
      },
      // Ignore scrolls inside the portal container so the user can scroll
      // the options list without closing the dropdown.
      ignoreScrollContainer: () => this.dropdownContainer
    });
  }

  private _unregisterScrollBehavior(): void {
    if (this._overlayScrollId !== undefined) {
      this.overlayScrollService.unregister(this._overlayScrollId);
      this._overlayScrollId = undefined;
    }
  }

  private _attachDropdownPortal(): void {
    if (!this.dropdownOutlet) {
      const { outlet, container } = this.dropdownPortal.createOutlet();
      this.dropdownOutlet = outlet;
      this.dropdownContainer = container;
    }
    this.dropdownPortal.bringToFront(this.dropdownContainer!);
    this.dropdownOutlet.detach();
    this.dropdownOutlet.attach(
      new TemplatePortal(this.dropdownPortalTpl(), this.viewContainerRef)
    );
  }

  private calculatePosition(): void {
    const host = this.elementRef.nativeElement;
    const trigger = host.querySelector('.select-trigger');
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const dropdownMaxHeight = 320;
    const dropdownMaxWidth = 350;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow) {
      this.verticalPosition = 'top';
    } else {
      this.verticalPosition = 'bottom';
    }

    const spaceRight = viewportWidth - rect.left;
    const spaceLeft = rect.right;

    if (spaceRight < dropdownMaxWidth && spaceLeft > spaceRight) {
      this.horizontalPosition = 'right';
    } else {
      this.horizontalPosition = 'left';
    }

    const style: Record<string, string> = {
      'min-width': `${rect.width}px`
    };

    if (this.verticalPosition === 'bottom') {
      style['top'] = `${rect.bottom + 4}px`;
    } else {
      style['bottom'] = `${viewportHeight - rect.top + 4}px`;
    }

    if (this.horizontalPosition === 'left') {
      style['left'] = `${rect.left}px`;
    } else {
      style['right'] = `${viewportWidth - rect.right}px`;
    }

    this.dropdownStyle = style;
  }
}
